import UnlockContact from '../components/Credits';



import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import ScheduleInterviewModal from './ScheduleInterviewModal';

const STATUS_OPTIONS = [
  { value: 'applied', label: 'Applied' },
  { value: 'screening', label: 'Screening' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview_round_1', label: 'Interview Round 1' },
  { value: 'interview_round_2', label: 'Interview Round 2' },
  { value: 'hr_round', label: 'HR Round' },
  { value: 'offer', label: 'Offer' },
  { value: 'joined', label: 'Joined' },
  { value: 'rejected', label: 'Rejected' },
];
const INTERVIEW_STATUSES = ['interview_round_1', 'interview_round_2', 'hr_round'];

const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ResumeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PinIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const formatSalary = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  if (num >= 100000) return `${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} Lakh`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatExperience = (years) => {
  if (years === null || years === undefined || years === '') return null;
  const num = Number(years);
  if (Number.isNaN(num)) return null;
  if (num < 1) return `${Math.round(num * 12)} mos`;
  return `${num} yr${num === 1 ? '' : 's'}`;
};

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow p-5">
      <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function EmployerCandidateProfile() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('application_id');
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [application, setApplication] = useState(null);
  const [jobPostings, setJobPostings] = useState([]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchCandidate = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/employer/candidates/${candidateId}`);
        setCandidate(res.data);
      } catch (err) {
        console.error('Failed to fetch candidate profile', err);
        setError(err.response?.data?.message || "Couldn't load this candidate's profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidate();
  }, [candidateId, navigate]);

  const loadApplication = async () => {
    if (!applicationId) return;
    try {
      const res = await api.get(`/applications/${applicationId}`);
      setApplication(res.data);
    } catch (err) {
      console.error('Failed to fetch application status', err);
    }
  };

  useEffect(() => {
    loadApplication();
    api.get('/jobs').then((res) => setJobPostings(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const handleStatusChange = async (newStatus) => {
    if (!application) return;
    setStatusError('');

    if (INTERVIEW_STATUSES.includes(newStatus)) {
      setShowScheduleModal(true);
      return;
    }

    setStatusUpdating(true);
    try {
      await api.put(`/applications/${applicationId}/status`, { status: newStatus });
      setApplication((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      setStatusError(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Field names below match EmployerCandidateController@show, which mirrors
  // CandidateDatabaseController::formatCandidate() — the shape already
  // proven working on the candidate-list cards this page is linked from.
  const experience = candidate ? formatExperience(candidate.experience_years) : null;
  const salary = candidate ? formatSalary(candidate.expected_salary) : null;
  const photoUrl = candidate?.profile_photo_url || null;
  const resumeUrl = candidate?.resume_url || null;
  const phone = candidate?.phone_number;

  return (
    <div className="min-h-screen bg-[#EFF6FF]">
      <Navbar user={user} />

      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 mb-4"
        >
          <ChevronLeftIcon /> Back
        </button>

        {loading ? (
          <p className="text-gray-500">Loading profile...</p>
        ) : error ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-red-500">{error}</div>
        ) : !candidate ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">Candidate not found.</div>
        ) : (
          <div className="space-y-4">
            {/* Header card */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-[#2563EB] text-white flex items-center justify-center font-bold text-2xl shrink-0 overflow-hidden">
                  {photoUrl ? (
                    <img src={photoUrl} alt={candidate.name} className="w-full h-full object-cover" />
                  ) : (
                    candidate.name?.charAt(0).toUpperCase() || '?'
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold text-[#0D1B3D]">{candidate.name}</h1>
                  {(candidate.current_designation || candidate.current_company) && (
                    <p className="text-sm text-gray-600 mt-0.5">
                      {candidate.current_designation}
                      {candidate.current_designation && candidate.current_company ? ' at ' : ''}
                      {candidate.current_company}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    {experience && <span>💼 {experience}</span>}
                    {salary && <span>💰 {salary}</span>}
                    {candidate.city && (
                      <span className="inline-flex items-center gap-1"><PinIcon className="w-3.5 h-3.5" /> {candidate.city}</span>
                    )}
                  </div>

                  {application && (
                    <div className="mt-3">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Application Status</label>
                      <select
                        value={application.status}
                        disabled={statusUpdating}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:bg-gray-100"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {statusError && <p className="text-xs text-red-500 mt-1">{statusError}</p>}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {candidate.contact_locked && (
                      <UnlockContact
                        candidateId={candidate.id}
                        onUnlocked={(c) => setCandidate((prev) => ({ ...prev, phone_number: c.phone, email: c.email, resume_url: c.resume_url, contact_locked: false }))}
                      />
                    )}
                    {phone && (
                      <a
                        href={`tel:${phone}`}
                        className="inline-flex items-center gap-1.5 bg-[#0F8B6D] hover:bg-[#0d755b] text-white text-sm font-semibold px-4 py-2 rounded-lg"
                      >
                        <PhoneIcon /> {phone}
                      </a>
                    )}
                    {candidate.email && (
                      <a
                        href={`mailto:${candidate.email}`}
                        className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-semibold px-4 py-2 rounded-lg"
                      >
                        <MailIcon /> {candidate.email}
                      </a>
                    )}
                    {resumeUrl && (
                      <a
                        href={resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 bg-white border border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF] text-sm font-semibold px-4 py-2 rounded-lg"
                      >
                        <ResumeIcon /> View Resume
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Skills */}
            {candidate.skills?.length > 0 && (
              <Section title="Skills">
                <div className="flex flex-wrap gap-1.5">
                  {candidate.skills.map((s, i) => (
                    <span
                      key={`${s.name}-${i}`}
                      className={`text-xs px-2.5 py-1 rounded-full ${
                        s.matched ? 'bg-yellow-100 text-yellow-800 font-semibold' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {s.name}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Work Experience */}
            <Section title="Work Experience">
              {candidate.experiences?.length > 0 ? (
                <ul className="space-y-3">
                  {candidate.experiences.map((e, i) => (
                    <li key={e.id ?? i} className="text-sm">
                      <p className="font-semibold text-[#0D1B3D]">
                        {e.job_title}
                        {e.currently_working && <span className="text-[#10B981] font-medium"> (current)</span>}
                      </p>
                      <p className="text-gray-500">{e.company_name}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400">No work experience listed.</p>
              )}
            </Section>

            {/* Education */}
            <Section title="Education">
              {candidate.educations?.length > 0 ? (
                <ul className="space-y-2">
                  {candidate.educations.map((e, i) => (
                    <li key={e.id ?? i} className="text-sm text-gray-700">
                      {e.degree}{e.institution ? `, ${e.institution}` : ''}{e.board_university ? ` (${e.board_university})` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400">No education listed.</p>
              )}
            </Section>

            {/* Certifications */}
            {candidate.certifications?.length > 0 && (
              <Section title="Certifications">
                <div className="flex flex-wrap gap-1.5">
                  {candidate.certifications.map((c, i) => (
                    <span key={c.id ?? i} className="text-xs px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[#10B981] font-medium">
                      {c.certificate_name}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Languages */}
            {candidate.languages?.length > 0 && (
              <Section title="Languages">
                <p className="text-sm text-gray-700">
                  {candidate.languages
                    .map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ''}`)
                    .join(' | ')}
                </p>
              </Section>
            )}

            {/* Preferred location */}
            {candidate.preferred_location && (
              <Section title="Preferred Location">
                <p className="text-sm text-gray-700">{candidate.preferred_location}</p>
              </Section>
            )}
          </div>
        )}

        {showScheduleModal && application && (
          <ScheduleInterviewModal
            jobPostings={jobPostings}
            initialJobPostingId={application.job_posting_id || ''}
            initialJobApplicationId={applicationId || ''}
            initialCandidateId={candidate?.id || ''}
            onClose={() => setShowScheduleModal(false)}
            onScheduled={loadApplication}
          />
        )}
      </div>
    </div>
  );
}